import React, { useState } from "react";
import { toast } from "sonner";
import { useT } from "@/lib/LanguageContext";
import { crmErrorMessage } from "../api/crmErrors";
import { DOMAIN_STATUS_KEYS } from "../contract";
import useSendingDomain from "../hooks/useSendingDomain";
import CrmCard from "../ui/CrmCard";
import CrmInput from "../ui/CrmInput";
import CrmButton from "../ui/CrmButton";
import CrmLoadFailure from "../ui/CrmLoadFailure";
import StatusBadge from "../ui/StatusBadge";
import { LABEL } from "../ui/classes";
import { CRM } from "../../../constants/testIds/crm";

const BADGE_TONE = { not_started: "neutral", pending: "warning", verified: "positive", failed: "danger" };

/**
 * "Sending domain" (spec Screen 6): the always-available Solunary subdomain, and — once
 * connected — the practice's own domain with its SPF/DKIM records, a Verify button while
 * `pending`/`failed`, and Disconnect once a custom domain exists. "Until verified, sends
 * use the subdomain" (decisions record: nothing bounces during setup).
 */
export default function SendingDomainCard() {
  const t = useT("crm");
  const tc = useT("common");
  const { domain, loadError, reload, connect, verify, disconnect } = useSendingDomain();
  const [candidate, setCandidate] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async (action) => {
    setBusy(true);
    try {
      await action();
    } catch (e) {
      toast.error(crmErrorMessage(e, t, tc));
    } finally {
      setBusy(false);
    }
  };

  if (loadError) return <CrmCard><CrmLoadFailure error={loadError} onRetry={reload} testId={CRM.emailError} /></CrmCard>;
  if (!domain) return <CrmCard className="animate-pulse h-32" />;

  const custom = domain.custom;
  const status = custom?.status;

  return (
    <CrmCard>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="font-display text-xl text-eh-ink">{t("email.domainTitle")}</p>
        {status && (
          <StatusBadge tone={BADGE_TONE[status] || "neutral"} testId={CRM.emailDomainStatus}>
            {t(DOMAIN_STATUS_KEYS[status])}
          </StatusBadge>
        )}
      </div>
      <p className="mt-2 text-sm text-eh-muted">{t("email.domainDefault", { address: domain.default.from_address })}</p>

      {!custom && (
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[14rem]">
            <label htmlFor="crm-domain-input" className={LABEL}>{t("email.domainInputLabel")}</label>
            <CrmInput id="crm-domain-input" value={candidate} onChange={(e) => setCandidate(e.target.value)}
              placeholder={t("email.domainConnectPlaceholder")} data-testid={CRM.emailDomainInput} />
          </div>
          <CrmButton disabled={busy || !candidate.trim()} data-testid={CRM.emailDomainConnect}
            onClick={() => run(() => connect({ domain: candidate.trim() }))}>
            {t("email.domainConnect")}
          </CrmButton>
        </div>
      )}

      {custom && (
        <div className="mt-4 space-y-3">
          <p className="text-sm text-eh-ink">{t("email.domainOwn", { domain: custom.domain })}</p>
          {status !== "verified" && (
            <div className="space-y-2">
              {(custom.records || []).map((r, i) => (
                <code key={`${r.type}-${r.host}-${i}`} data-testid={CRM.emailDomainRecord(i)}
                  className="block rounded-lg bg-eh-cream px-3 py-2 text-xs text-eh-ink break-all">
                  {r.type} {r.host} · {r.value}
                </code>
              ))}
              <p className="text-xs text-eh-muted">{t("email.domainFallback")}</p>
            </div>
          )}
          <div className="flex flex-wrap gap-3">
            {status !== "verified" && (
              <CrmButton variant="outline" disabled={busy} data-testid={CRM.emailDomainVerify} onClick={() => run(verify)}>
                {t("email.domainVerify")}
              </CrmButton>
            )}
            <CrmButton variant="link" disabled={busy} data-testid={CRM.emailDomainDisconnect}
              onClick={() => run(async () => { await disconnect(); setCandidate(""); })}>
              {t("email.domainDisconnect")}
            </CrmButton>
          </div>
        </div>
      )}
    </CrmCard>
  );
}
